import { PLAYFIELD_WIDTH } from '@shared/gameTypes';
import {
  WAVES_CEILING_Y,
  WAVES_FLOOR_Y,
  WAVES_HORIZONTAL_SPEED_MUL,
  WAVES_PLAYFIELD_HEIGHT,
} from '@shared/wavesChart';

/**
 * Side-scrolling Waves playfield geometry.
 *
 * The shared engine speaks in playfield units (top-left origin,
 * {@link PLAYFIELD_WIDTH} × {@link WAVES_PLAYFIELD_HEIGHT}). The renderer
 * draws into a canvas whose CSS size depends on the user's window.
 * This module owns the mapping between the two so the renderer and
 * input layers never have to re-derive it.
 *
 * Waves uses the FULL canvas (no central trapezoid like Guitar) — the
 * playfield gets letterboxed when the canvas aspect ratio doesn't
 * match the playfield, with the longest dimension stretched edge to
 * edge and the other dimension centred.
 */
export interface WavesLayout {
  /** CSS-pixel width per playfield-X unit. */
  scaleX: number;
  /** CSS-pixel height per playfield-Y unit. */
  scaleY: number;
  /** CSS-pixel X of playfield X=0. */
  offsetX: number;
  /** CSS-pixel Y of playfield Y=0. */
  offsetY: number;
  /** Visible playfield width in CSS pixels. */
  widthPx: number;
  /** Visible playfield height in CSS pixels. */
  heightPx: number;
}

/**
 * Compute the playfield-to-canvas mapping for the current canvas
 * size. The playfield is scaled UNIFORMLY (same scale in X and Y) and
 * centred, so circles render as circles and slopes look right at any
 * aspect ratio.
 */
export function computeWavesLayout(canvasWidth: number, canvasHeight: number): WavesLayout {
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    return {
      scaleX: 1,
      scaleY: 1,
      offsetX: 0,
      offsetY: 0,
      widthPx: 0,
      heightPx: 0,
    };
  }
  const sx = canvasWidth / PLAYFIELD_WIDTH;
  const sy = canvasHeight / WAVES_PLAYFIELD_HEIGHT;
  const scale = Math.min(sx, sy);
  const widthPx = PLAYFIELD_WIDTH * scale;
  const heightPx = WAVES_PLAYFIELD_HEIGHT * scale;
  const offsetX = (canvasWidth - widthPx) / 2;
  const offsetY = (canvasHeight - heightPx) / 2;
  return {
    scaleX: scale,
    scaleY: scale,
    offsetX,
    offsetY,
    widthPx,
    heightPx,
  };
}

/**
 * Map a playfield X coordinate to a canvas X. Waves doesn't shift the
 * playfield horizontally during play — the obstacles' approach is
 * modelled in beat space, not X space — so this is a straight linear
 * map.
 */
export function playfieldXToCanvas(layout: WavesLayout, playfieldX: number): number {
  return layout.offsetX + playfieldX * layout.scaleX;
}

/**
 * Map a playfield Y coordinate to a canvas Y. Engine state's
 * `state.playerY`, obstacle `gapTopY`/`gapBottomY`/`centerY`, and the
 * corridor bounds {@link WAVES_CEILING_Y} / {@link WAVES_FLOOR_Y} all
 * go through this helper.
 */
export function playfieldYToCanvas(layout: WavesLayout, playfieldY: number): number {
  return layout.offsetY + playfieldY * layout.scaleY;
}

/**
 * Beat-to-canvas-X mapping for a scrolling obstacle. The player is
 * anchored at a fixed canvas X ({@link PLAYER_ANCHOR_X_FRAC} of the
 * visible playfield), and obstacles approach from the right at
 * {@link OBSTACLE_BEATS_VISIBLE_AHEAD} beats per visible playfield
 * width. An obstacle at `obs.beat = currentBeat` lands at the
 * player's anchor X; one at `obs.beat = currentBeat + visibleBeats`
 * starts at the right edge of the playfield.
 */
export const PLAYER_ANCHOR_X_FRAC = 0.18;

/** Beats from player anchor to the right edge at default scroll speed. */
const BASE_OBSTACLE_BEATS_VISIBLE_AHEAD = 5;

/** Faster horizontal scroll — fewer beats to cross the playfield width. */
export const OBSTACLE_BEATS_VISIBLE_AHEAD =
  BASE_OBSTACLE_BEATS_VISIBLE_AHEAD / WAVES_HORIZONTAL_SPEED_MUL;

/**
 * Compute the canvas X where an obstacle at `obstacleBeat` should
 * draw when the audio clock is at `currentBeat`. Scroll rate is a
 * single constant `pxPerBeat` for the entire trajectory: an obstacle
 * approaches the player anchor at the same speed it then leaves it.
 *
 * Constant rate matters perceptually — the playfield reads as the
 * ship moving forward through stationary hazards. If passed obstacles
 * decelerated (as they did when the trailing range was squeezed into
 * the same beat budget as the leading range), they'd look like
 * they're "sticking" to the player's wake, which contradicts the
 * forward-flight illusion.
 */
export function obstacleCanvasX(
  layout: WavesLayout,
  currentBeat: number,
  obstacleBeat: number,
): number {
  const anchorX = layout.offsetX + layout.widthPx * PLAYER_ANCHOR_X_FRAC;
  const visibleRight = layout.offsetX + layout.widthPx;
  const leadingRange = visibleRight - anchorX;
  const pxPerBeat = leadingRange / OBSTACLE_BEATS_VISIBLE_AHEAD;
  return anchorX + (obstacleBeat - currentBeat) * pxPerBeat;
}

/**
 * Inverse of {@link obstacleCanvasX}: the beat whose scroll position
 * lands at `canvasX` when the audio clock is at `currentBeat`. The
 * terrain renderer uses this to find the beat span covering the visible
 * width so it can sample the floor/ceiling polylines edge to edge.
 */
export function canvasXToBeat(
  layout: WavesLayout,
  currentBeat: number,
  canvasX: number,
): number {
  const anchorX = layout.offsetX + layout.widthPx * PLAYER_ANCHOR_X_FRAC;
  const visibleRight = layout.offsetX + layout.widthPx;
  const leadingRange = visibleRight - anchorX;
  const pxPerBeat = leadingRange / OBSTACLE_BEATS_VISIBLE_AHEAD;
  return currentBeat + (canvasX - anchorX) / pxPerBeat;
}

/**
 * Canvas X of the player's bounding-circle centre. Constant during a
 * loop — the player only moves vertically; obstacles scroll past on
 * the X axis.
 */
export function playerCanvasX(layout: WavesLayout): number {
  return layout.offsetX + layout.widthPx * PLAYER_ANCHOR_X_FRAC;
}

/**
 * Convenience: the visible corridor's canvas-Y bounds, derived from
 * the engine's playfield-Y constants.
 */
export function corridorCanvasBounds(layout: WavesLayout): { topY: number; bottomY: number } {
  return {
    topY: playfieldYToCanvas(layout, WAVES_CEILING_Y),
    bottomY: playfieldYToCanvas(layout, WAVES_FLOOR_Y),
  };
}
